import { useEffect, useRef, useState } from 'react';
import { createChart, AreaSeries, ColorType, createSeriesMarkers, type IChartApi, type ISeriesApi, type ISeriesMarkersPluginApi, type UTCTimestamp, type Time } from 'lightweight-charts';
import { useMarkets } from '@/state/markets';
import { getTicker } from '@/content/tickers';
import { priceAt, getImpulsesForTicker, tickerHasActiveImpulse } from '@/engine/prices';
import { priceFmt, pct } from '@/lib/format';
import { cn } from '@/lib/cn';

// Historical window — render up to 30 days back. Tiered sampling so total
// point count stays manageable (~10K points): coarse far-back, fine near-now.
const HISTORY_FAR_DAYS = 30;          // 30 days back
const HISTORY_NEAR_HOURS = 6;          // last 6 hours at fine resolution
const FAR_SAMPLE_INTERVAL_S = 600;     // 10-minute samples for far history
const NEAR_SAMPLE_INTERVAL_S = 5;      // 5-second samples for the recent slice
const LIVE_UPDATE_MS = 1000;           // 1s live update cadence

/**
 * TradingView lightweight-charts wrapper. Replaces the SVG line chart with
 * a real financial-tier renderer. Same deterministic priceAt() under the
 * hood, just rendered via canvas for proper scrubbing/zoom/feel.
 */

export function Chart() {
  const tickerId = useMarkets((s) => s.activeTickerId);
  const positions = useMarkets((s) => s.positions);
  const openOrders = useMarkets((s) => s.openOrders);
  const cfg = getTicker(tickerId);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const costBasisLineRef = useRef<ReturnType<ISeriesApi<'Area'>['createPriceLine']> | null>(null);
  const limitLinesRef = useRef<ReturnType<ISeriesApi<'Area'>['createPriceLine']>[]>([]);
  const lastTimeRef = useRef<number>(0);
  const [headerNow, setHeaderNow] = useState(() => Date.now());
  const [tfSec, setTfSec] = useState<number>(() => {
    // Persist across reloads via localStorage so the player's preferred zoom sticks
    const saved = typeof window !== 'undefined' ? localStorage.getItem('nasdaq.one:chart-tfsec') : null;
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 3600;
  });
  useEffect(() => {
    try { localStorage.setItem('nasdaq.one:chart-tfsec', String(tfSec)); } catch {/* private mode */}
  }, [tfSec]);

  // Cost-basis horizontal line — shows your average entry price for the active
  // ticker so break-even is visible. Toggles on/off as positions change.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    // Remove old line first
    if (costBasisLineRef.current) {
      try { series.removePriceLine(costBasisLineRef.current); } catch {}
      costBasisLineRef.current = null;
    }
    const pos = positions.find((p) => p.ticker === tickerId);
    if (!pos || pos.qty <= 0) return;
    costBasisLineRef.current = series.createPriceLine({
      price: pos.avgPrice,
      color: '#8a93a3',
      lineWidth: 1,
      lineStyle: 2, // dashed
      axisLabelVisible: true,
      title: `cost ${pos.qty}`,
    });
  }, [tickerId, positions]);

  // Open-limit-order horizontal lines — show resting orders at their limit price
  // so you can see what you've staged before they fill.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    // Tear down any existing lines first
    for (const line of limitLinesRef.current) {
      try { series.removePriceLine(line); } catch {}
    }
    limitLinesRef.current = [];
    const ours = openOrders.filter((o) => o.ticker === tickerId);
    for (const o of ours) {
      if (o.type === 'limit' && o.limitPrice != null) {
        const line = series.createPriceLine({
          price: o.limitPrice,
          color: o.side === 'buy' ? '#4ec9b0' : '#f06b6b',
          lineWidth: 1,
          lineStyle: 1, // dotted
          axisLabelVisible: true,
          title: `${o.side === 'buy' ? 'BUY' : 'SELL'} ${o.qty}`,
        });
        limitLinesRef.current.push(line);
      } else if (o.type === 'stop' && o.stopPrice != null) {
        const line = series.createPriceLine({
          price: o.stopPrice,
          color: '#f0a020', // amber for stops
          lineWidth: 1,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: `STOP ${o.side.toUpperCase()} ${o.qty}`,
        });
        limitLinesRef.current.push(line);
      } else if (o.type === 'trail' && o.trailPct != null && o.trailPeak != null) {
        const triggerPrice = o.side === 'sell'
          ? o.trailPeak * (1 - o.trailPct)
          : o.trailPeak * (1 + o.trailPct);
        const line = series.createPriceLine({
          price: triggerPrice,
          color: '#c084fc', // violet for trailing stops
          lineWidth: 1,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: `TRAIL ${o.side.toUpperCase()} ${o.qty} (${(o.trailPct * 100).toFixed(1)}%)`,
        });
        limitLinesRef.current.push(line);
      }
    }
  }, [tickerId, openOrders]);

  // Init chart ONCE on mount; data updates handled separately so ticker switching
  // doesn't tear down the chart (smoother UX + no flash).
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // Read current theme tokens off the document so we match
    const styles = getComputedStyle(document.documentElement);
    const accent = styles.getPropertyValue('--accent-primary').trim() || '#ffb84d';
    const muted = styles.getPropertyValue('--text-muted').trim() || '#5e4515';
    const subtle = styles.getPropertyValue('--border-subtle').trim() || '#1f1f1f';

    const chart = createChart(container, {
      // Force a known-good locale — some browsers report 'en-US@posix' for the
      // env locale, which Date.toLocaleString rejects, and lightweight-charts'
      // default tick formatter then throws RangeError on every render.
      localization: { locale: 'en-US' },
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: muted,
        fontFamily: 'JetBrains Mono, ui-monospace, Menlo, monospace',
        fontSize: 11,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: subtle },
        horzLines: { color: subtle },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        borderColor: subtle,
      },
      rightPriceScale: { borderColor: subtle },
      crosshair: {
        vertLine: { color: muted, width: 1, style: 3 },
        horzLine: { color: muted, width: 1, style: 3 },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: { time: true, price: false } },
    });

    const series = chart.addSeries(AreaSeries, {
      lineColor: accent,
      topColor: `${accent}55`,
      bottomColor: `${accent}05`,
      lineWidth: 2,
    });

    chartRef.current = chart;
    seriesRef.current = series;
    markersRef.current = createSeriesMarkers(series, []);

    // Resize observer
    const ro = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });
    });
    ro.observe(container);

    // initial size
    chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      markersRef.current = null;
    };
  }, []); // mount once

  // Reload data whenever the active ticker changes
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !cfg) return;
    series.applyOptions({
      priceFormat: {
        type: 'price',
        precision: cfg.baseline > 100 ? 2 : 4,
        minMove: cfg.baseline > 100 ? 0.01 : 0.0001,
      },
    });
    const nowSec = Math.floor(Date.now() / 1000);
    const farFromSec = nowSec - HISTORY_FAR_DAYS * 86_400;
    const nearFromSec = nowSec - HISTORY_NEAR_HOURS * 3600;
    const data: { time: UTCTimestamp; value: number }[] = [];
    // Far history at coarse resolution
    for (let t = farFromSec; t < nearFromSec; t += FAR_SAMPLE_INTERVAL_S) {
      data.push({ time: t as UTCTimestamp, value: priceAt(cfg, t * 1000) });
    }
    // Near history at fine resolution
    for (let t = nearFromSec; t <= nowSec; t += NEAR_SAMPLE_INTERVAL_S) {
      data.push({ time: t as UTCTimestamp, value: priceAt(cfg, t * 1000) });
    }
    series.setData(data);
    lastTimeRef.current = nowSec;

    // Set markers: news-impulse arrows + your own buy/sell fills.
    // Player fills get distinct shapes (square) so they read as "I did this"
    // vs "the market did this."
    const impulses = getImpulsesForTicker(cfg.ticker, farFromSec * 1000, nowSec * 1000);
    // Cap to last 20 fills so the chart doesn't get visually drowned in markers
    const fills = useMarkets.getState().recentFills.filter((f) => f.ticker === cfg.ticker).slice(0, 20);
    if (markersRef.current) {
      const all = [
        ...impulses.map((imp) => ({
          time: Math.floor(imp.startMs / 1000) as UTCTimestamp,
          position: (imp.logDelta >= 0 ? 'belowBar' : 'aboveBar') as 'belowBar' | 'aboveBar',
          color: imp.logDelta >= 0 ? '#4ec9b0' : '#f06b6b',
          shape: (imp.logDelta >= 0 ? 'arrowUp' : 'arrowDown') as 'arrowUp' | 'arrowDown',
          text: imp.logDelta >= 0 ? '📈' : '📉',
        })),
        ...fills.map((f) => ({
          time: Math.floor(f.at / 1000) as UTCTimestamp,
          position: (f.side === 'buy' ? 'belowBar' : 'aboveBar') as 'belowBar' | 'aboveBar',
          color: f.side === 'buy' ? '#4ec9b0' : '#f06b6b',
          shape: 'square' as const,
          text: `${f.side === 'buy' ? 'B' : 'S'} ${f.qty}`,
        })),
      ];
      // markers must be sorted by time for lightweight-charts
      all.sort((a, b) => Number(a.time) - Number(b.time));
      markersRef.current.setMarkers(all);
    }

    // Set default visible range LAST — after data + markers — so lightweight-charts
    // doesn't auto-fit-content when those land. Window driven by selected timeframe.
    chartRef.current?.timeScale().setVisibleRange({
      from: (nowSec - tfSec) as UTCTimestamp,
      to: nowSec as UTCTimestamp,
    });
  }, [tickerId, cfg, tfSec]);

  // Re-apply visible range when timeframe changes for the current ticker.
  useEffect(() => {
    if (!chartRef.current) return;
    const nowSec = Math.floor(Date.now() / 1000);
    chartRef.current.timeScale().setVisibleRange({
      from: (nowSec - tfSec) as UTCTimestamp,
      to: nowSec as UTCTimestamp,
    });
  }, [tfSec]);

  // Live updates
  useEffect(() => {
    if (!cfg) return;
    let lastMarkerCount = -1;
    const id = setInterval(() => {
      setHeaderNow(Date.now());
      const series = seriesRef.current;
      if (!series) return;
      const nowSec = Math.floor(Date.now() / 1000);
      // Update at sample-interval cadence so the line advances
      if (nowSec - lastTimeRef.current >= NEAR_SAMPLE_INTERVAL_S) {
        const t = nowSec as UTCTimestamp;
        series.update({ time: t, value: priceAt(cfg, nowSec * 1000) });
        lastTimeRef.current = nowSec;
      } else {
        // Just update the latest point with the latest price (gives the
        // "breathing" effect within the sample interval)
        const t = lastTimeRef.current as UTCTimestamp;
        series.update({ time: t, value: priceAt(cfg, Date.now()) });
      }
      // Refresh impulse + own-fill markers when either set's count changes
      const fromSec = nowSec - HISTORY_FAR_DAYS * 86_400;
      const impulses = getImpulsesForTicker(cfg.ticker, fromSec * 1000, nowSec * 1000);
      // Cap to last 20 fills so the chart doesn't get visually drowned in markers
    const fills = useMarkets.getState().recentFills.filter((f) => f.ticker === cfg.ticker).slice(0, 20);
      const totalCount = impulses.length + fills.length;
      if (totalCount !== lastMarkerCount && markersRef.current) {
        lastMarkerCount = totalCount;
        const all = [
          ...impulses.map((imp) => ({
            time: Math.floor(imp.startMs / 1000) as UTCTimestamp,
            position: (imp.logDelta >= 0 ? 'belowBar' : 'aboveBar') as 'belowBar' | 'aboveBar',
            color: imp.logDelta >= 0 ? '#4ec9b0' : '#f06b6b',
            shape: (imp.logDelta >= 0 ? 'arrowUp' : 'arrowDown') as 'arrowUp' | 'arrowDown',
            text: imp.logDelta >= 0 ? '📈' : '📉',
          })),
          ...fills.map((f) => ({
            time: Math.floor(f.at / 1000) as UTCTimestamp,
            position: (f.side === 'buy' ? 'belowBar' : 'aboveBar') as 'belowBar' | 'aboveBar',
            color: f.side === 'buy' ? '#4ec9b0' : '#f06b6b',
            shape: 'square' as const,
            text: `${f.side === 'buy' ? 'B' : 'S'} ${f.qty}`,
          })),
        ];
        all.sort((a, b) => Number(a.time) - Number(b.time));
        markersRef.current.setMarkers(all);
      }
    }, LIVE_UPDATE_MS);
    return () => clearInterval(id);
  }, [cfg]);

  if (!cfg) return <div className="p-4 text-[var(--text-muted)]">Unknown ticker</div>;

  const livePrice = priceAt(cfg, headerNow);
  // % change measured against the price 24h ago — feels like a session/day change
  // (Old formula compared to a 2026-01-01 anchor which produced absurd +87000% reads.)
  const ref = priceAt(cfg, headerNow - 24 * 60 * 60 * 1000);
  const change = ref > 0 ? livePrice / ref - 1 : 0;
  const positive = change >= 0;

  return (
    <div className="flex h-full flex-col bg-[var(--bg-surface)]">
      <div className="flex items-baseline justify-between gap-4 border-b border-[var(--border-subtle)] px-4 py-2">
        <div className="flex items-baseline gap-3">
          <span className="text-mono-md font-bold text-[var(--text-primary)]">{cfg.id}</span>
          <span className="hidden sm:inline text-mono-xs text-[var(--text-muted)] truncate max-w-[300px]">
            {cfg.category === 'company' && 'name' in cfg ? cfg.name : (cfg.question ?? cfg.name ?? '')}
          </span>
          {tickerHasActiveImpulse(cfg.ticker) && (
            <span
              className="inline-flex items-center gap-1 rounded border border-[var(--warning)] bg-[var(--warning)]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--warning)] animate-pulse"
              title="active news impulse moving this ticker"
            >
              🔥 active news
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-3">
          <span className="text-mono-lg tabular-nums text-[var(--text-primary)]">
            {priceFmt(livePrice)}
          </span>
          <span
            className={cn(
              'text-mono-sm tabular-nums',
              positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]',
            )}
          >
            {pct(change)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1">
        {[
          { label: '5m', sec: 300 },
          { label: '15m', sec: 900 },
          { label: '1h', sec: 3600 },
          { label: '4h', sec: 14_400 },
          { label: '1d', sec: 86_400 },
          { label: '7d', sec: 604_800 },
        ].map((tf) => (
          <button
            key={tf.sec}
            onClick={() => setTfSec(tf.sec)}
            className={cn(
              'rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors',
              tfSec === tf.sec
                ? 'bg-[var(--accent-primary)] text-black'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-secondary)]',
            )}
          >
            {tf.label}
          </button>
        ))}
      </div>
      <div ref={containerRef} className="flex-1 min-h-0" />
    </div>
  );
}
